import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { TryadLogo } from "@/components/TryadLogo";
import { motion, AnimatePresence } from "framer-motion";

const MIN_PASSWORD_LENGTH = 6;

const inputClass =
  "w-full bg-card border border-border rounded-xl px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors";

const slide = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
  transition: { duration: 0.25 },
};

export default function LoginPage() {
  const { login, register, resetPassword, isFirebaseConfigured } = useAuth();
  const [mode, setMode] = useState("login"); // 'login' | 'register' | 'forgot'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);

  const switchMode = (next) => {
    setMode(next);
    setPassword("");
    setError("");
    setNotice("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");

    if (!email.trim()) return setError("Escribe tu email.");

    if (mode === "forgot") {
      setLoading(true);
      const result = await resetPassword(email);
      setLoading(false);
      if (!result.success) return setError(result.error);
      return setNotice(
        "Te hemos enviado un email para restablecer tu contraseña. Revisa también la carpeta de spam."
      );
    }

    if (mode === "register" && password.length < MIN_PASSWORD_LENGTH) {
      return setError(`La contraseña debe tener al menos ${MIN_PASSWORD_LENGTH} caracteres.`);
    }

    setLoading(true);
    const result = mode === "register" ? await register(email, password) : await login(email, password);
    setLoading(false);
    // On success AuthContext swaps this screen out, so there is nothing to do.
    if (!result.success) setError(result.error);
  };

  if (!isFirebaseConfigured) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 text-center">
        <TryadLogo size={48} className="mb-4" />
        <h1 className="text-xl font-bold text-foreground mb-2">Falta la configuración</h1>
        <p className="text-sm text-muted-foreground max-w-xs">
          No se ha encontrado la configuración de Firebase. Revisa las variables de entorno{" "}
          <code className="text-primary">REACT_APP_FIREBASE_*</code>.
        </p>
      </div>
    );
  }

  const titles = {
    login: { heading: "Inicia sesión", sub: "Entra para ver tu rutina" },
    register: { heading: "Crea tu cuenta", sub: "Tu rutina, en todos tus dispositivos" },
    forgot: { heading: "Recuperar contraseña", sub: "Te enviaremos un enlace por email" },
  }[mode];

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6">
      <motion.div
        className="w-full max-w-sm"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="flex flex-col items-center mb-10">
          <TryadLogo size={48} className="mb-3" />
          <h1 className="text-2xl font-bold text-foreground tracking-tight">TRYAD</h1>
          <p className="text-muted-foreground text-sm mt-1">Tu app de entrenamiento</p>
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={mode} {...slide}>
            <div className="mb-6 text-center">
              <h2 className="font-semibold text-foreground">{titles.heading}</h2>
              <p className="text-xs text-muted-foreground mt-1">{titles.sub}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  autoComplete="email"
                  autoFocus
                  className={inputClass}
                />
              </div>

              {mode !== "forgot" && (
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">
                    Contraseña
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={
                      mode === "register" ? `Mínimo ${MIN_PASSWORD_LENGTH} caracteres` : "Tu contraseña"
                    }
                    autoComplete={mode === "register" ? "new-password" : "current-password"}
                    className={inputClass}
                  />
                </div>
              )}

              {error && (
                <p className="text-xs text-red-400 bg-red-950/30 border border-red-900/40 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}

              {notice && (
                <p className="text-xs text-emerald-400 bg-emerald-950/30 border border-emerald-900/40 rounded-lg px-3 py-2">
                  {notice}
                </p>
              )}

              <button
                type="submit"
                disabled={loading || !email || (mode !== "forgot" && !password)}
                className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {loading
                  ? "Un momento..."
                  : mode === "register"
                    ? "Crear cuenta"
                    : mode === "forgot"
                      ? "Enviar email"
                      : "Entrar"}
              </button>
            </form>

            <div className="mt-6 space-y-2 text-center text-xs">
              {mode === "login" && (
                <>
                  <button
                    onClick={() => switchMode("forgot")}
                    className="text-muted-foreground hover:text-foreground transition-colors block w-full"
                  >
                    He olvidado mi contraseña
                  </button>
                  <button
                    onClick={() => switchMode("register")}
                    className="text-muted-foreground hover:text-foreground transition-colors block w-full"
                  >
                    ¿No tienes cuenta? <span className="text-primary font-medium">Regístrate</span>
                  </button>
                </>
              )}

              {mode !== "login" && (
                <button
                  onClick={() => switchMode("login")}
                  className="text-muted-foreground hover:text-foreground transition-colors block w-full"
                >
                  ← Volver a iniciar sesión
                </button>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
